import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { 
  Users, MessageCircle, Clock, TrendingUp, 
  Activity, BarChart3, PieChart as PieChartIcon, Loader, UserCheck, Star, ArrowUpRight, ArrowDownRight,
  Calendar, ChevronDown, Filter, RefreshCw, Building2, FileText, Target, Zap, 
  TrendingDown, Award, Shield, Globe, Database, Settings, Eye, BarChart4,
  UserPlus, UserMinus, Settings as SettingsIcon, Key, Lock, Unlock, Image
} from 'lucide-react';

function AdminPage() {
  const { theme, currentThemeName } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      .animate-fade-in-up {
        animation: fadeInUp 0.6s ease-out;
      }
      .animate-slide-in-right {
        animation: slideInRight 0.6s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      if (!loading) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      // Fetch real analytics data instead of static data
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (!token) {
        throw new Error(t('admin.authenticationRequired'));
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const response = await fetch(`${getApiBaseUrl()}/api/analytics/recent-activities?timeframe=7d`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Real analytics data loaded for admin:', data);
      
      setStats(data);
    } catch (err) {
      console.error('Error in admin dashboard:', err);
      setError(t('admin.failedToLoadData'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, changeType = 'up', subtitle, onClick }) => (
    <div 
      style={{
        background: currentThemeName === 'dark' 
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
        border: currentThemeName === 'dark'
          ? '1px solid rgba(59, 130, 246, 0.2)'
          : currentThemeName === 'beige'
          ? '1px solid rgba(139, 69, 19, 0.15)'
          : '1px solid rgba(59, 130, 246, 0.1)',
        borderRadius: 20,
        padding: 28,
        boxShadow: currentThemeName === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
          : currentThemeName === 'beige'
          ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
          : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onClick}
      onMouseEnter={onClick ? (e) => {
        e.target.style.transform = 'translateY(-4px)';
        e.target.style.boxShadow = currentThemeName === 'dark'
          ? '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.2)'
          : currentThemeName === 'beige'
          ? '0 12px 40px rgba(139, 69, 19, 0.15), 0 0 0 1px rgba(139, 69, 19, 0.1)'
          : '0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.1)';
      } : undefined}
      onMouseLeave={onClick ? (e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = currentThemeName === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
          : currentThemeName === 'beige'
          ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
          : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)';
      } : undefined}
    >
      {/* Background gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: currentThemeName === 'dark'
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.03) 0%, rgba(160, 82, 45, 0.03) 100%)'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(147, 51, 234, 0.03) 100%)',
        zIndex: 0
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: currentThemeName === 'dark'
              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)'
              : currentThemeName === 'beige'
              ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.1) 0%, rgba(160, 82, 45, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icon size={24} color={currentThemeName === 'dark' ? '#60a5fa' : currentThemeName === 'beige' ? '#8d6e63' : '#3b82f6'} />
          </div>
          {change && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {changeType === 'up' ? (
                <ArrowUpRight size={16} color="#10b981" />
              ) : (
                <ArrowDownRight size={16} color="#ef4444" />
              )}
              <span style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                color: changeType === 'up' ? '#10b981' : '#ef4444' 
              }}>
                {change}
              </span>
            </div>
          )}
        </div>
        
        <div style={{ marginBottom: 8 }}>
          <div style={{ 
            fontSize: 32, 
            fontWeight: 700, 
            color: currentThemeName === 'dark' ? 'white' : theme.text,
            lineHeight: 1.2
          }}>
            {value}
          </div>
        </div>
        
        <div style={{ 
          fontSize: 14, 
          fontWeight: 600, 
          color: currentThemeName === 'dark' ? '#e5e7eb' : theme.textSecondary,
          marginBottom: subtitle ? 4 : 0
        }}>
          {title}
        </div>
        
        {subtitle && (
          <div style={{ 
            fontSize: 12, 
            color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary 
          }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  const AdminCard = ({ title, description, icon: Icon, onClick, color = '#3b82f6' }) => (
    <div 
      style={{
        background: currentThemeName === 'dark' 
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
        border: currentThemeName === 'dark'
          ? '1px solid rgba(59, 130, 246, 0.2)'
          : currentThemeName === 'beige'
          ? '1px solid rgba(139, 69, 19, 0.15)'
          : '1px solid rgba(59, 130, 246, 0.1)',
        borderRadius: 16,
        padding: 24,
        boxShadow: currentThemeName === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
          : currentThemeName === 'beige'
          ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
          : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-4px)';
        e.target.style.boxShadow = currentThemeName === 'dark'
          ? '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(59, 130, 246, 0.2)'
          : currentThemeName === 'beige'
          ? '0 12px 40px rgba(139, 69, 19, 0.15), 0 0 0 1px rgba(139, 69, 19, 0.1)'
          : '0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.1)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = currentThemeName === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
          : currentThemeName === 'beige'
          ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
          : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${color}20 0%, ${color}30 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={24} color={color} />
        </div>
        <div>
          <div style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: currentThemeName === 'dark' ? 'white' : theme.text,
            marginBottom: 4
          }}>
            {title}
          </div>
          <div style={{ 
            fontSize: 14, 
            color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary 
          }}>
            {description}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: currentThemeName === 'dark'
          ? '#0f172a'  // Dark blue background
          : currentThemeName === 'beige'
          ? '#f5f5dc'  // Light beige background
          : '#ffffff', // White background
        color: theme.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} className="animate-spin" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 18, fontWeight: 600 }}>{t('admin.loadingAdminDashboard')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: currentThemeName === 'dark'
          ? '#0f172a'  // Dark blue background
          : currentThemeName === 'beige'
          ? '#f5f5dc'  // Light beige background
          : '#ffffff', // White background
        color: theme.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{t('admin.errorLoadingAdminDashboard')}</div>
          <div style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 24 }}>{error}</div>
          <button
            onClick={fetchStats}
            style={{
              padding: '12px 24px',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {t('admin.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: currentThemeName === 'dark'
        ? '#0f172a'  // Dark blue background
        : currentThemeName === 'beige'
        ? '#f5f5dc'  // Light beige background
        : '#ffffff', // White background
      color: theme.text,
      padding: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background gradient */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '300px',
        background: currentThemeName === 'dark'
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.08) 0%, rgba(160, 82, 45, 0.08) 100%)'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%)',
        zIndex: 0
      }} />

      <div style={{ 
        maxWidth: 1400, 
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div className="animate-fade-in-up" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 32,
          padding: '24px 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Oakland AI Logo */}
            <div
              className="logo-section"
              onClick={() => navigate('/fullchat')}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/fullchat');
                }
              }}
              title="Go to main page"
              style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
            >
              <div className="logo-text" style={{
                fontSize: '28px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.02em'
              }}>Oakland AI</div>
            </div>
            <div style={{ 
              width: 1, 
              height: 40, 
              background: theme.border,
              margin: '0 16px'
            }} />
            <div>
              <h1 style={{ 
                fontSize: 32, 
                fontWeight: 700, 
                color: currentThemeName === 'dark' 
                  ? 'white' 
                  : currentThemeName === 'beige'
                  ? '#5d4037'
                  : '#1f2937',
                margin: '0 0 8px 0'
              }}>
                {t('admin.title')}
              </h1>
              <p style={{
                fontSize: 16,
                color: currentThemeName === 'dark'
                  ? '#e5e7eb'
                  : currentThemeName === 'beige'
                  ? '#8d6e63'
                  : '#6b7280',
                margin: 0
              }}>
                {t('admin.subtitle')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={fetchStats}
              disabled={refreshing}
              style={{
                padding: '12px 20px',
                borderRadius: 12,
                background: currentThemeName === 'dark'
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)'
                  : currentThemeName === 'beige'
                  ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.1) 0%, rgba(160, 82, 45, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.3)'
                  : currentThemeName === 'beige'
                  ? '1px solid rgba(139, 69, 19, 0.2)'
                  : '1px solid rgba(59, 130, 246, 0.2)',
                color: currentThemeName === 'dark' ? '#60a5fa' : currentThemeName === 'beige' ? '#8d6e63' : '#3b82f6',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? t('admin.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="animate-fade-in-up" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: 24, 
          marginBottom: 32 
        }}>
          <StatCard
            title={t('admin.totalUsers')}
            value={stats?.userStats?.totalUsers || 0}
            icon={Users}
            subtitle={t('admin.registeredUsers')}
          />
          <StatCard
            title={t('admin.activeUsers')}
            value={stats?.userStats?.activeUsers || 0}
            icon={UserCheck}
            subtitle={t('admin.currentlyOnline')}
          />
          <StatCard
            title={t('admin.totalChats')}
            value={stats?.userStats?.totalChats || 0}
            icon={MessageCircle}
            subtitle={t('admin.thisWeek')}
          />
          <StatCard
            title={t('admin.avgSession')}
            value={stats?.userStats?.avgSessionDuration || '0 min'}
            icon={Clock}
            subtitle={t('admin.userEngagement')}
          />
        </div>

        {/* Admin Actions Grid */}
        <div className="animate-fade-in-up" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: 24, 
          marginBottom: 32 
        }}>
          <AdminCard
            title={t('admin.userManagement')}
            description={t('admin.manageUsersDesc')}
            icon={UserPlus}
            color="#3b82f6"
            onClick={() => navigate('/userlist')}
          />
          <AdminCard
            title={t('admin.analyticsDashboard')}
            description={t('admin.viewAnalyticsDesc')}
            icon={BarChart3}
            color="#10b981"
            onClick={() => navigate('/analytics')}
          />
          <AdminCard
            title={t('admin.contentEditor')}
            description={t('admin.contentEditorDesc')}
            icon={Image}
            color="#8b5cf6"
            onClick={() => navigate('/admin/content-edit')}
          />
          <AdminCard
            title={t('admin.systemSettings')}
            description={t('admin.systemSettingsDesc')}
            icon={Settings}
            color="#f59e0b"
            onClick={() => navigate('/admin/settings')}
          />
          <AdminCard
            title={t('admin.securityAccess')}
            description={t('admin.securityDesc')}
            icon={Shield}
            color="#ef4444"
            onClick={() => navigate('/admin/security')}
          />
        </div>

        {/* Quick Stats */}
        <div className="animate-fade-in-up" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 20 
        }}>
          <div style={{
            background: currentThemeName === 'dark' 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : currentThemeName === 'beige'
              ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
            border: currentThemeName === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(59, 130, 246, 0.1)',
            borderRadius: 16,
            padding: 20,
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: currentThemeName === 'dark' ? 'white' : theme.text,
              marginBottom: 8
            }}>
              {stats?.userStats?.userSatisfaction || 0}
            </div>
            <div style={{
              fontSize: 14,
              color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary
            }}>
              {t('admin.userSatisfaction')}
            </div>
          </div>

          <div style={{
            background: currentThemeName === 'dark' 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : currentThemeName === 'beige'
              ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
            border: currentThemeName === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(59, 130, 246, 0.1)',
            borderRadius: 16,
            padding: 20,
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: currentThemeName === 'dark' ? 'white' : theme.text,
              marginBottom: 8
            }}>
              {stats?.userStats?.newUsersThisWeek || 0}
            </div>
            <div style={{
              fontSize: 14,
              color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary
            }}>
              {t('admin.newUsersThisWeek')}
            </div>
          </div>

          <div style={{
            background: currentThemeName === 'dark'
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
              : currentThemeName === 'beige'
              ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
            border: currentThemeName === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(59, 130, 246, 0.1)',
            borderRadius: 16,
            padding: 20,
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: currentThemeName === 'dark' ? 'white' : theme.text,
              marginBottom: 8
            }}>
              {stats?.businessTypeData?.length || 0}
            </div>
            <div style={{
              fontSize: 14,
              color: currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary
            }}>
              {t('admin.businessTypes')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;